import { FeatureCart, formatPromoDisplay } from '@mdlbeasts/nofomo.ui.web';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { type ReactNode } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { useLocale } from 'hooks/useLocale';
import { useTranslatedText } from 'hooks/useTranslatedText';
import {
  type LocalCartItem,
  type Ticket,
  type TicketAddonItem,
} from 'restful/tickets';
import { getPaidCatalogueAddons } from 'restful/tickets/addon.utils';
import { TicketStatusFragment } from 'types/api/dato-operations.types';

import ResponsiveDrawerDialog from 'components/@common/ResponsiveDrawerDialog';
import Icon from 'components/@icons/Icon';
import AmountSelector from 'components/@tickets/AmountSelector';
import { useTicketInfo } from 'components/@tickets/hooks';

import TicketAddonRow from './TicketAddonRow';
import TicketPerkList from './TicketPerkList';
import { isTicketStatusActive, toTicketStatusType } from './utils';

interface TicketDetailContentProps {
  ticket: Ticket | undefined;
  cartItem: LocalCartItem | undefined;
  showAddonSelection: boolean;
  canSelectAddons: boolean;
  onChangeAddon?: (addon: TicketAddonItem, quantity: number) => void;
}

const TicketDetailContent = ({
  ticket,
  cartItem,
  showAddonSelection,
  canSelectAddons,
  onChangeAddon,
}: TicketDetailContentProps) => {
  const localize = useTranslatedText();
  const description = localize(ticket?.description);
  const parentQuantity = cartItem?.quantity ?? 0;
  const paidAddons = showAddonSelection ? getPaidCatalogueAddons(ticket) : [];

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      {!!description && (
        <Typography
          variant="small"
          sx={{
            color: 'text.secondary',
          }}
        >
          {description}
        </Typography>
      )}

      <TicketPerkList perks={ticket?.perks} />

      {!!paidAddons.length && (
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{
              color: 'common.white',
            }}
          >
            <FormattedMessage id="tickets.cart.addons.label" />
          </Typography>
          <Stack
            sx={{
              gap: 2,
            }}
            divider={<Divider sx={{ borderColor: 'divider' }} />}
          >
            {paidAddons.map((addon) => (
              <TicketAddonRow
                key={addon.id}
                addon={addon}
                parentQuantity={parentQuantity}
                quantity={
                  cartItem?.addons?.find(
                    (item) => item.catalogueItemId === addon.id,
                  )?.quantity ?? 0
                }
                canSelect={canSelectAddons}
                onChangeAddon={onChangeAddon}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

interface Props {
  open: boolean;
  ticket: Ticket | undefined;
  cartItem: LocalCartItem | undefined;
  status?: TicketStatusFragment;
  showAddonSelection: boolean;
  canSelectAddons: boolean;
  showParentQuantity: boolean;
  actions?: ReactNode;
  disableEnforceFocus?: boolean;
  onClose: () => void;
  onAddParent: () => void;
  onRemoveParent: () => void;
  onChangeAddon: (addon: TicketAddonItem, quantity: number) => void;
}
const TicketDetailDialog = ({
  open,
  ticket,
  cartItem,
  status,
  showAddonSelection,
  canSelectAddons,
  showParentQuantity,
  actions,
  disableEnforceFocus = false,
  onClose,
  onAddParent,
  onRemoveParent,
  onChangeAddon,
}: Props) => {
  const { locale } = useLocale();
  const intl = useIntl();
  const localize = useTranslatedText();
  const parentQuantity = cartItem?.quantity ?? 0;
  const { isSoldOut: isParentSoldOut } = useTicketInfo(ticket?.id ?? '');
  const isStatusActive = isTicketStatusActive(status);
  const canPurchase = !isStatusActive && !isParentSoldOut;
  const showPurchaseActions = canPurchase && (showParentQuantity || !!actions);
  const showStatus = !canPurchase;
  const promoLabel = ticket?.promoMessage
    ? formatPromoDisplay(localize(ticket.promoMessage) ?? '', locale)
    : '';

  return (
    <ResponsiveDrawerDialog
      isOpen={open}
      onClose={onClose}
      hideHeaderActions
      disableEnforceFocus={disableEnforceFocus}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Stack
          sx={{
            flexGrow: 1,
            minWidth: 0,
            gap: 0.75,
          }}
        >
          {!!promoLabel && (
            <Typography
              variant="extraSmall"
              fontWeight={600}
              sx={{
                width: 'fit-content',
                maxWidth: '100%',
                whiteSpace: 'normal',
                textAlign: 'start',
                color: 'common.white',
              }}
            >
              {promoLabel}
            </Typography>
          )}
          <Typography variant="h6" component="h2">
            {localize(ticket?.name)}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          variant="filled"
          onClick={onClose}
          aria-label={intl.formatMessage({ id: 'navigation.close' })}
          sx={{
            flexShrink: 0,
          }}
        >
          <Icon name="Close" fontSize={24} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3 }}>
        <TicketDetailContent
          ticket={ticket}
          cartItem={cartItem}
          showAddonSelection={showAddonSelection}
          canSelectAddons={canSelectAddons && canPurchase}
          onChangeAddon={onChangeAddon}
        />
      </DialogContent>
      {(showPurchaseActions || showStatus) && (
        <>
          <Divider sx={{ mx: 3, borderColor: 'divider' }} />
          <DialogActions
            sx={{
              mt: 3,
            }}
          >
            {showStatus ? (
              <Box sx={{ width: '100%' }}>
                <FeatureCart.TicketItem.Status
                  color={status?.color?.hex}
                  content={status?.content}
                  type={toTicketStatusType(status?.statusType)}
                  schedule={status?.schedule}
                  soldOut={isParentSoldOut}
                />
              </Box>
            ) : showParentQuantity ? (
              <AmountSelector
                size="medium"
                quantity={parentQuantity}
                isRemoveDisabled={parentQuantity <= 0}
                onRemove={onRemoveParent}
                onAdd={onAddParent}
                center={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="span"
                      sx={{
                        color: 'global01.50',
                      }}
                    >
                      <FormattedNumber value={parentQuantity} />
                    </Typography>
                    <Typography
                      variant="small"
                      fontWeight="bold"
                      component="span"
                      sx={{
                        color: 'common.white',
                      }}
                    >
                      <FormattedMessage
                        id={
                          parentQuantity === 1
                            ? 'tickets.cart.addon.ticket.single.label'
                            : 'tickets.cart.addon.ticket.multiple.label'
                        }
                      />
                    </Typography>
                  </Box>
                }
                sx={{
                  width: '100%',
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {actions}
              </Box>
            )}
          </DialogActions>
        </>
      )}
    </ResponsiveDrawerDialog>
  );
};

export default TicketDetailDialog;
